import type { PrimitiveValue } from "@skilitics/nautilus-core";
import { isReferencedValue } from "@skilitics/nautilus-core-designer-data";

import type { ResolvedValueSignature } from "../resolveValueSignature.ts";
import { useGetValueSubtypeDefinition } from "../useGetValueSubtypeDefinition.ts";
import { useValueConfigSummary } from "./useValueConfigSummary.ts";

const UNREADABLE_OPERAND = "?";

/**
 * How one operand of an expression reads in a summary: a bound operand names
 * what it points at, a literal reads as its subject's subtype formats it — a
 * position stored from 0 counted from 1, a locale code as its language. Shared,
 * so every summary over the same expression agrees.
 */
export const useDescribeValueOperand = () => {
  const summarise = useValueConfigSummary();
  const getValueSubtypeDefinition = useGetValueSubtypeDefinition();

  return (operand: unknown, signature: ResolvedValueSignature | undefined): string => {
    const summarised = summarise(operand);
    if (summarised === undefined) return UNREADABLE_OPERAND;
    if (isReferencedValue(operand)) return String(summarised);

    const formatLiteral = (signature && getValueSubtypeDefinition(signature).formatLiteral) ?? String;
    return formatLiteral(summarised as PrimitiveValue, signature?.subtype, signature?.subtypeSpec);
  };
};
